// src/components/common/PageHeader.jsx
//
// One header for every page, so titles line up across the app:
//
//   Sidebar destinations (lists, overviews):
//     <PageHeader icon={Biotech} title="Udstyr" subtitle="…" actions={<Button …/>} />
//
//   Sub-pages (details, create/edit):
//     <PageHeader onBack={…} title={room.name} badges={<Chip …/>} subtitle="…" actions={…} />
//
// `icon` should be the same icon the sidebar uses for the page. Sub-pages show a
// back button instead of an icon; `avatar` replaces the icon for people/things
// that have a picture of their own.
//
// On a record's own page (URL ends in its id) the title also names the
// breadcrumb: "Hjem > Udstyr > Heka UnicLine S". Pass `crumb` when the title
// isn't plain text or isn't the record's name (e.g. on its /edit page).
// Note: theme.spacing(1) is 0.25rem here (not MUI's 8px), hence the larger numbers.
import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useBreadcrumbLabel } from '../../context/BreadcrumbContext';

const RECORD_ID = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

const ICON_SIZE = 40;

export default function PageHeader({
  icon: Icon,
  avatar,
  title,
  subtitle,
  badges,
  onBack,
  actions,
  crumb,
  mb = 6,
  sx,
}) {
  const { t } = useTranslation(['common']);
  // /equipment/<id> → the title names <id>; /equipment/<id>/edit → only an explicit `crumb` does
  const [prev, last] = useLocation().pathname.split('/').filter(Boolean).slice(-2);
  const onRecord = Boolean(last && RECORD_ID.test(last));
  const recordId = onRecord ? last : (last === 'edit' && prev && RECORD_ID.test(prev) ? prev : null);
  useBreadcrumbLabel(recordId, crumb ?? (onRecord && typeof title === 'string' ? title : null));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 4,
        rowGap: 3,
        mb,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flex: '1 1 320px', minWidth: 0 }}>
        {onBack && (
          <Tooltip title={t('common:buttons.back')}>
            <IconButton
              onClick={onBack}
              aria-label={t('common:buttons.back')}
              sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', flexShrink: 0 }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}
        {avatar}
        {!onBack && !avatar && Icon && (
          <Icon sx={{ fontSize: ICON_SIZE, color: 'primary.main', flexShrink: 0 }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 3, rowGap: 1 }}>
            <Typography variant="h3" component="h1" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
              {title}
            </Typography>
            {badges}
          </Box>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, ml: 'auto' }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}
